import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Row, Col, Card, Form } from 'react-bootstrap';
import { useCurrencyFormatter } from '../../../../../../../hooks/useCurrencyFormatter';
import { useNamespacedTranslation } from '../../../../../../../hooks/useNamespacedTranslation';
import FlairIcon from '../../../../../../../atomic/atoms/FlairIcon';
import { formatDateShort } from '../../../../../../../utils/dateUtils';
import './SalaryChangeComponent.css';
import { getStatusBadgeVariant } from './utils';
import { frequencyOptions } from './types';

type SalaryTilesProps = {
  activeSalaries: any[];
  selectedSalaryId: string;
  updateAmountOnly: boolean;
  loading: boolean;
  error: any;
  hasPermissionIssue: boolean;
  hasNoSalary: boolean;
  onSalarySelection: (salaryId: string) => void;
};

export const SalaryTiles: React.FC<
  React.PropsWithChildren<SalaryTilesProps>
> = ({
  activeSalaries,
  selectedSalaryId,
  updateAmountOnly,
  loading,
  error,
  hasPermissionIssue,
  hasNoSalary,
  onSalarySelection,
}) => {
  const t = useNamespacedTranslation('changeRequests.requestForm.salaryChange');
  const formatCurrency = useCurrencyFormatter();

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-3">
        <div
          className="spinner-border spinner-border-sm text-primary"
          role="status">
          <span className="visually-hidden">{t('loadingSalaries')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-3 text-danger">
        <small>
          {t('errorLoadingSalaries')} {error.message}
        </small>
      </div>
    );
  }

  if (hasNoSalary) {
    return (
      <div className="text-center py-3 text-warning">
        <FlairIcon
          icon="information-circle-outline"
          size="lg"
          className="mb-2"
        />
        <div>
          <Form.Label className="text-warning mb-2">
            {t('noSalaryIssue.title')}
          </Form.Label>
        </div>
        <small className="text-muted">
          {t('noSalaryIssue.description')}
          <br />
          <strong>{t('solution')}</strong> {t('noSalaryIssue.solution')}
        </small>
      </div>
    );
  }

  if (hasPermissionIssue) {
    return (
      <div className="text-center py-3 text-warning">
        <FlairIcon
          icon="information-circle-outline"
          size="lg"
          className="mb-2"
        />
        <div>
          <Form.Label className="text-warning mb-2">
            {t('permissionIssue.noAccessToSalaryInfo')}
          </Form.Label>
        </div>
        <small className="text-muted">
          {t('permissionIssue.permissionMessage')}
          <br />
          <strong>{t('solution')}</strong> {t('permissionIssue.solution')}
        </small>
      </div>
    );
  }

  if (activeSalaries.length === 0) {
    return (
      <div className="text-center py-3 text-muted">
        <small>{t('noActiveSalaries')}</small>
      </div>
    );
  }

  return (
    <Row className="g-2">
      {activeSalaries.map((salary) => (
        <Col key={salary.Id} md={4}>
          <Card
            className={`salary-tile h-100 ${
              selectedSalaryId === salary.Id ? 'selected' : ''
            } ${!updateAmountOnly ? 'disabled' : ''}`}
            onClick={() => updateAmountOnly && onSalarySelection(salary.Id)}>
            <Card.Body className="p-3 pb-3">
              <div className="fw-bold text-center mb-1">
                {formatCurrency({
                  value: salary.flair__Amount__c,
                  currency: salary.CurrencyIsoCode,
                })}
              </div>
              <div className="text-muted text-center small mb-2">
                {t(
                  'options.' +
                    frequencyOptions.find(
                      (option) => option.value === salary.flair__Salary_Type__c,
                    )?.label,
                )}
              </div>
              <div className="text-center small text-muted mb-2">
                <div>
                  {t('dateFrom')} {formatDateShort(salary.flair__Start_Date__c)}
                </div>
                <div>
                  {t('dateTo')}{' '}
                  {salary.flair__End_Date__c
                    ? formatDateShort(salary.flair__End_Date__c)
                    : '?'}
                </div>
              </div>
              <div className="text-center">
                <Badge
                  variant={getStatusBadgeVariant(
                    salary.flair__Formula_Status__c,
                  )}
                  className="small">
                  {salary.flair__Formula_Status__c}
                </Badge>
              </div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
};
